import {
  ArrowBack,
  FilterAltOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  InfoOutlined,
  MoreVert,
  ViewColumnOutlined,
  WidthFullOutlined,
  WidthNormalOutlined,
  WidthWideOutlined,
} from "@mui/icons-material";
import { Box, Container, IconButton, Stack, Tooltip } from "@mui/material";
import { Button, paletteVar, Typography } from "@platform/design-system";
import { useNotification } from "@platform/hooks";
import { CoverageTypeEnum } from "@platform/types";
import {
  RfpCoverage,
  RfpData,
  coverageTypeToBackendName,
} from "@platform/plan-grid-core";

import { BtxExport } from "../export";
import { getMissingPlanLevelFields } from "../util/planLevelValidation";

import { CoverageSelector } from "./CoverageSelector";
import { CoverageCompletionButton } from "./CoverageCompletionButton";
import { CoverageStatusSelectButton } from "./CoverageStatusSelectButton";

import type {
  CoverageStatus,
  RequiredFieldsValidationResult,
} from "@platform/plan-grid-core";
import type { BenefitPlanTypeEnum } from "@platform/api";

// ============================================================================
// Types & Interfaces
// ============================================================================

/** Plan-column width presets: wide = 100% (default), medium = 75%, narrow = 50%. */
export type ColumnWidthPreset = "narrow" | "medium" | "wide";

/** Width-preset toggle options, rendered left of the full screen button. */
const COLUMN_WIDTH_OPTIONS: {
  preset: ColumnWidthPreset;
  label: string;
  icon: React.ReactNode;
}[] = [
  { preset: "narrow", label: "Narrow columns", icon: <WidthNormalOutlined /> },
  { preset: "medium", label: "Medium columns", icon: <WidthWideOutlined /> },
  { preset: "wide", label: "Wide columns", icon: <WidthFullOutlined /> },
];

export interface IUtilityBarProps {
  /** RFP data for export */
  rfp: RfpData;
  /** ECO RFP ID (internal BTX RFP ID) for fetching coverage types */
  rfpId: string;
  /** Persists pending edits before the export reads server data */
  onBeforeExport: () => Promise<void>;
  /** Currently selected coverage */
  selectedCoverage?: RfpCoverage;
  /** Callback when a coverage is selected */
  onCoverageSelect?: (coverageType: CoverageTypeEnum) => void;
  /** Required fields validation result for the selected coverage */
  requiredFieldsValidation?: RequiredFieldsValidationResult;
  /** True when two plan groups share a Current Policy; blocks marking complete (ADR 10.22). */
  hasDuplicateCurrentPolicy?: boolean;
  /** Autosave status text */
  lastUpdated?: string;
  /** Fields that failed to save due to incomplete data */
  unsavedFields?: string[];
  /** Callback for back navigation */
  onBack?: VoidFunction;
  /** When true, show the coverage status select UI instead of the coverage completion toggle button. */
  useCoverageStatusSelect?: boolean;
  /**
   * True only when the inline Plan Level dropdown is rendered (the doc-map
   * column path). Plan Level can only block Mark Complete where a UI exists
   * to set it — normal/benchmark BTX modes have no dropdown, so this stays
   * false there and the requirement is skipped.
   */
  isPlanLevelDropdownEnabled?: boolean;
  /** External coverage status from document library context. */
  status?: CoverageStatus;
  /** Additional action handlers */
  onFilter?: VoidFunction;
  onMore?: VoidFunction;
  onFullScreen?: VoidFunction;
  /** When true, the full screen toggle shows the "exit" affordance. */
  isFullScreen?: boolean;
  /** Active plan-column width preset (drives the toggle highlight). */
  columnWidth?: ColumnWidthPreset;
  /** Callback to change the plan-column width preset; when provided, the toggle is shown. */
  onColumnWidthChange?: (preset: ColumnWidthPreset) => void;
  onColumnMenu?: VoidFunction;
}

// ============================================================================
// Components
// ============================================================================

/**
 * Navigation controls component
 */
const NavigationControls: React.FC<{
  ecoRfpId: string;
  selectedCoverage?: RfpCoverage;
  onCoverageSelect: (coverageType: CoverageTypeEnum) => void;
  onBack?: VoidFunction;
}> = ({ ecoRfpId, selectedCoverage, onCoverageSelect, onBack }) => (
  <Stack direction="row" alignItems="center" spacing={2}>
    {onBack && (
      <Button
        variant="tertiary"
        size="sm"
        startIcon={<ArrowBack />}
        onClick={onBack}
      >
        Back to overview
      </Button>
    )}
    <CoverageSelector
      ecoRfpId={ecoRfpId}
      selectedCoverage={selectedCoverage}
      onCoverageSelect={onCoverageSelect}
    />
  </Stack>
);

/**
 * Coverage completion control — either the status select or the legacy
 * complete/incomplete toggle. Extracted so ActionButtons stays simple.
 */
const CompletionControl: React.FC<{
  selectedCoverage: RfpCoverage;
  requiredFieldsValidation?: RequiredFieldsValidationResult;
  hasDuplicateCurrentPolicy?: boolean;
  useCoverageStatusSelect: boolean;
  isPlanLevelDropdownEnabled: boolean;
  status?: CoverageStatus;
}> = ({
  selectedCoverage,
  requiredFieldsValidation,
  hasDuplicateCurrentPolicy,
  useCoverageStatusSelect,
  isPlanLevelDropdownEnabled,
  status,
}) => {
  const { showError } = useNotification();
  const hasEligibilityClasses =
    selectedCoverage.benefitPlans?.some(
      (plan) => (plan.eligibilityClasses?.length ?? 0) > 0,
    ) ?? false;
  // Only enforce Plan Level where the dropdown that sets it actually renders
  // (the doc-map column path) — elsewhere there's no UI to satisfy it.
  const missingPlanLevelFields = isPlanLevelDropdownEnabled
    ? getMissingPlanLevelFields(selectedCoverage.benefitPlans)
    : [];
  const hasAllRequiredFields =
    (requiredFieldsValidation?.isValid ?? true) &&
    missingPlanLevelFields.length === 0;
  const missingFields = [
    ...(requiredFieldsValidation?.missingFields ?? []),
    ...missingPlanLevelFields,
  ];

  if (useCoverageStatusSelect) {
    return (
      <CoverageStatusSelectButton
        coverageType={selectedCoverage.coverageType}
        productType={selectedCoverage.productType}
        hasEligibilityClasses={hasEligibilityClasses}
        hasAllRequiredFields={hasAllRequiredFields}
        missingFields={missingFields}
        hasDuplicateCurrentPolicy={hasDuplicateCurrentPolicy}
        status={status}
        onSuccess={() => undefined}
        onError={() => {
          showError(
            "Failed to update coverage status. Please try again.",
            false,
          );
        }}
      />
    );
  }

  return (
    <CoverageCompletionButton
      coverageName={coverageTypeToBackendName(selectedCoverage.type)}
      coverageType={selectedCoverage.coverageType}
      productType={selectedCoverage.productType as BenefitPlanTypeEnum}
      hasEligibilityClasses={hasEligibilityClasses}
      hasAllRequiredFields={hasAllRequiredFields}
      missingFields={missingFields}
      hasDuplicateCurrentPolicy={hasDuplicateCurrentPolicy}
    />
  );
};

//  Action buttons component
const ActionButtons: React.FC<{
  rfp: RfpData;
  rfpId: string;
  onBeforeExport: () => Promise<void>;
  selectedCoverage?: RfpCoverage;
  requiredFieldsValidation?: RequiredFieldsValidationResult;
  hasDuplicateCurrentPolicy?: boolean;
  lastUpdated: string;
  unsavedFields?: string[];
  useCoverageStatusSelect?: boolean;
  isPlanLevelDropdownEnabled?: boolean;
  status?: CoverageStatus;
  onFilter?: VoidFunction;
  onMore?: VoidFunction;
  onFullScreen?: VoidFunction;
  isFullScreen?: boolean;
  columnWidth?: ColumnWidthPreset;
  onColumnWidthChange?: (preset: ColumnWidthPreset) => void;
  onColumnMenu?: VoidFunction;
}> = ({
  rfp,
  rfpId,
  onBeforeExport,
  selectedCoverage,
  requiredFieldsValidation,
  hasDuplicateCurrentPolicy,
  lastUpdated,
  unsavedFields = [],
  useCoverageStatusSelect = false,
  isPlanLevelDropdownEnabled = false,
  status,
  onFilter,
  onMore,
  onColumnMenu,
  onFullScreen,
  isFullScreen,
  columnWidth,
  onColumnWidthChange,
}) => (
  <Stack direction="row" alignItems="center" spacing={2}>
    {unsavedFields.length > 0 ? (
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ flexShrink: 0 }}
      >
        <Typography variant="caption" color="danger">
          changes not saved
        </Typography>
        <Tooltip
          title={
            <Box sx={{ p: 0.5 }}>
              <Box sx={{ mb: 0.5 }}>Missing required values:</Box>
              {unsavedFields.map((field) => (
                <Box key={field}>• {field}</Box>
              ))}
            </Box>
          }
          arrow
        >
          <InfoOutlined
            sx={{ fontSize: 16, color: "error.main", cursor: "pointer" }}
          />
        </Tooltip>
      </Stack>
    ) : lastUpdated ? (
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ flexShrink: 0 }}
      >
        {lastUpdated}
      </Typography>
    ) : null}
    {/* Action Icons*/}
    {onFilter && (
      <IconButton size="small" onClick={onFilter} color="default">
        <FilterAltOutlined />
      </IconButton>
    )}
    {onColumnMenu && (
      <IconButton size="small" onClick={onColumnMenu} color="default">
        <ViewColumnOutlined />
      </IconButton>
    )}
    {onColumnWidthChange &&
      COLUMN_WIDTH_OPTIONS.map(({ preset, label, icon }) => (
        <Tooltip key={preset} title={label}>
          <IconButton
            size="small"
            onClick={() => onColumnWidthChange(preset)}
            color={columnWidth === preset ? "primary" : "default"}
            aria-label={label}
            aria-pressed={columnWidth === preset}
          >
            {icon}
          </IconButton>
        </Tooltip>
      ))}
    {onFullScreen && (
      <Tooltip title={isFullScreen ? "Exit full screen" : "Full screen"}>
        <IconButton
          size="small"
          onClick={onFullScreen}
          color="default"
          aria-label={isFullScreen ? "Exit full screen" : "Full screen"}
          aria-pressed={isFullScreen}
        >
          {isFullScreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
        </IconButton>
      </Tooltip>
    )}
    {onMore && (
      <IconButton size="small" onClick={onMore} color="default">
        <MoreVert />
      </IconButton>
    )}
    {selectedCoverage && (
      <CompletionControl
        selectedCoverage={selectedCoverage}
        requiredFieldsValidation={requiredFieldsValidation}
        hasDuplicateCurrentPolicy={hasDuplicateCurrentPolicy}
        useCoverageStatusSelect={useCoverageStatusSelect}
        isPlanLevelDropdownEnabled={isPlanLevelDropdownEnabled}
        status={status}
      />
    )}
    <BtxExport
      rfpName={rfp.name}
      ecoRfpId={rfpId}
      onBeforeExport={onBeforeExport}
    />
  </Stack>
);

// ============================================================================
// Main Component
// ============================================================================

/**
 * Table top content component with navigation, progress, and actions
 */
export const UtilityBar: React.FC<IUtilityBarProps> = ({
  rfp,
  rfpId,
  onBeforeExport,
  selectedCoverage,
  onCoverageSelect,
  requiredFieldsValidation,
  hasDuplicateCurrentPolicy,
  lastUpdated = "",
  unsavedFields,
  onBack,
  onFilter,
  onMore,
  onColumnMenu,
  onFullScreen,
  isFullScreen,
  columnWidth,
  onColumnWidthChange,
  useCoverageStatusSelect = false,
  isPlanLevelDropdownEnabled = false,
  status,
}) => (
  <Box
    sx={{
      width: "100vw",
      mx: "calc(50% - 50vw)",
      borderBottom: 1,
      borderColor: "divider",
      py: 1,
      backgroundColor: paletteVar("background-default", "#FAFAFA"),
    }}
  >
    <Container>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 0.75,
          gap: 2,
        }}
      >
        {/* Left section: Navigation and Progress */}
        <Stack direction="row" alignItems="center" spacing={3}>
          {onCoverageSelect && (
            <NavigationControls
              ecoRfpId={rfpId}
              selectedCoverage={selectedCoverage}
              onCoverageSelect={onCoverageSelect}
              onBack={onBack}
            />
          )}
        </Stack>

        {/* Right section: Actions */}
        <ActionButtons
          rfp={rfp}
          rfpId={rfpId}
          onBeforeExport={onBeforeExport}
          selectedCoverage={selectedCoverage}
          requiredFieldsValidation={requiredFieldsValidation}
          hasDuplicateCurrentPolicy={hasDuplicateCurrentPolicy}
          lastUpdated={lastUpdated}
          unsavedFields={unsavedFields}
          onFilter={onFilter}
          onMore={onMore}
          onColumnMenu={onColumnMenu}
          onFullScreen={onFullScreen}
          isFullScreen={isFullScreen}
          columnWidth={columnWidth}
          onColumnWidthChange={onColumnWidthChange}
          useCoverageStatusSelect={useCoverageStatusSelect}
          isPlanLevelDropdownEnabled={isPlanLevelDropdownEnabled}
          status={status}
        />
      </Box>
    </Container>
  </Box>
);

// Set display name for better debugging
UtilityBar.displayName = "UtilityBar";
export default UtilityBar;
